import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output } from '@angular/core';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { type MonthViewDay } from 'calendar-utils';
import { Subject } from 'rxjs';
import { EventsListPopupComponent } from '../events-list/events-list-popup.component';
import { SmallEventsPreviewListComponent } from '../events-list/small-events-preview-list.component';
import { Constants } from '@frontendlib/common/types/Constants';

@Component({
  selector: 'stch-calendar-month-day-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './calendar-month-day-cell.component.html',
  styleUrls: ['./calendar-month-day-cell.component.less'],
  imports: [
    CommonModule,
    DragNDropModule,
    PopoverDirective,
    EventsListPopupComponent,
    SmallEventsPreviewListComponent,
    Button2Component,
    DateFormatPipe,
  ],
})
export class CalendarMonthDayCellComponent {
  public readonly day = input.required<MonthViewDay>();
  public readonly selected = input<boolean>(false);
  public readonly popupDisabled = input<boolean>(false);
  public readonly eventsAppearance = input<'list' | 'dots'>('dots');
  public readonly maxEventsPerCell = input<number>(0);

  protected readonly eventClicked = output<CalendarEvent>();
  protected readonly eventDblClicked = output<CalendarEvent>();

  public readonly popoverClose = new Subject<void>();
  public readonly elementRef = inject(ElementRef);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  protected readonly defaultEventColor = Constants.DEFAULT_EVENT_COLOR;

  protected readonly events = computed(() => this.day().events.map((event) => event.meta));

  // SBUX-829: `day().date` is a real instant at the display-zone midnight laid out by the calendar's
  // DateAdapter; the date-only `dayNumber` format reads browser-local parts, so render its display
  // (account) zone wall clock — otherwise the number is off by one when account is east of browser
  // and disagrees with the (display-zone) weekday header and today marker.
  protected readonly displayDate = computed(() =>
    toDisplayZoneWallClock(this.day().date, this.displayTimeZoneService.timeZone()),
  );

  protected readonly visibleEventCount = computed(() => {
    const dayEvents = this.day().events;
    const maxEvents = this.maxEventsPerCell();

    if (maxEvents === 0 || dayEvents.length <= maxEvents) {
      return dayEvents.length;
    }

    return maxEvents - 1;
  });

  protected readonly hasMoreEvents = computed(() => {
    const dayEvents = this.day().events;
    const maxEvents = this.maxEventsPerCell();

    return maxEvents > 0 && dayEvents.length > maxEvents;
  });
}
